/**
 * DIGIPIN Encoder issues — dark-theme form + open issues list.
 * Uses GeoVera public chrome for site-wide visual consistency.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import SendIcon from '@mui/icons-material/Send';
import { API_ORIGIN } from '../../config/api';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import Footer from '../Footer';

export interface DigipinIssue {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  plugin_version?: string | null;
  created_at: string;
  updated_at?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  bug: 'Bug',
  feature: 'Feature idea',
  question: 'Question',
  other: 'Other',
};

const fieldSx = {
  '& .MuiInputLabel-root': { color: 'rgba(234, 242, 223, 0.78)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#d7f5ad' },
  '& .MuiOutlinedInput-root': {
    color: '#eaf2df',
    backgroundColor: 'rgba(16,41,35,0.7)',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(215,245,173,0.32)' },
    '&:hover fieldset': { borderColor: 'rgba(215,245,173,0.55)' },
    '&.Mui-focused fieldset': { borderColor: '#b7d9a8' },
    '& input::placeholder, & textarea::placeholder': {
      color: 'rgba(234,242,223,0.45)',
      opacity: 1,
    },
  },
  '& .MuiFormHelperText-root': { color: 'rgba(234,242,223,0.55)' },
  '& .MuiSelect-icon': { color: 'rgba(234,242,223,0.75)' },
} as const;

const statusColor = (s: string) => {
  if (s === 'open') return 'warning';
  if (s === 'in_progress') return 'info';
  if (s === 'resolved') return 'success';
  return 'default';
};

async function ensureCsrfCookie() {
  try {
    await fetch(`${API_ORIGIN}/api/csrf-token`, { credentials: 'include' });
  } catch {
    /* optional */
  }
}

function getCsrf(): string | null {
  const m = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
  return m ? m[2] : null;
}

const DigipinEncoderIssuesPage = () => {
  const [issues, setIssues] = useState<DigipinIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  const [contactEmail, setContactEmail] = useState('');
  const [pluginVersion, setPluginVersion] = useState('2.0.0');
  const [qgisVersion, setQgisVersion] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_ORIGIN}/api/digipin-encoder/issues`, {
        credentials: 'include',
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        throw new Error('Issues API is unavailable. Try again after deploy.');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load issues');
      setIssues(data.issues || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    ensureCsrfCookie();
  }, [load]);

  const canSubmit = title.trim().length >= 5 && description.trim().length >= 10 && !submitting;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await ensureCsrfCookie();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      const csrf = getCsrf();
      if (csrf) headers['X-CSRF-Token'] = csrf;

      const res = await fetch(`${API_ORIGIN}/api/digipin-encoder/issues`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          category,
          contactEmail: contactEmail.trim() || undefined,
          pluginVersion: pluginVersion.trim() || undefined,
          qgisVersion: qgisVersion.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Could not post issue');
      }
      setSuccess(data.message || 'Issue posted. Thank you!');
      setTitle('');
      setDescription('');
      setCategory('bug');
      setContactEmail('');
      setQgisVersion('');
      await load();
      // Scroll to top of list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={publicPageSx}>
      <PublicSiteChrome title="DIGIPIN Issues" subtitle="Report bugs & ideas" active="issues" />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <BugReportIcon sx={{ color: '#d7f5ad', fontSize: 32 }} />
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Report a problem
          </Typography>
        </Stack>
        <Typography sx={{ color: '#b8cabd', mb: 3, maxWidth: 560, lineHeight: 1.65 }}>
          Found a bug in the QGIS DIGIPIN Encoder? Have a feature idea? Post below — no login
          required. Include steps and your plugin version when you can.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, bgcolor: 'rgba(239,83,80,0.12)', color: '#ffcdd2' }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2, bgcolor: 'rgba(102,187,106,0.12)', color: '#c8e6c9' }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Dark glass form — high contrast, always visible submit */}
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            mb: 5,
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 3,
            bgcolor: 'rgba(16,41,35,0.72)',
            border: '1px solid rgba(215,245,173,0.22)',
            boxShadow: '0 20px 48px rgba(0,0,0,0.35)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#edf5e8', mb: 2.5 }}>
            Post a new issue
          </Typography>

          <Stack spacing={2.25}>
            <TextField
              label="Short title"
              required
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Verify Key fails after paste"
              helperText="At least 5 characters"
              sx={fieldSx}
            />
            <TextField
              label="What happened?"
              required
              fullWidth
              multiline
              minRows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="1. What you did&#10;2. What you expected&#10;3. What actually happened (error text helps)"
              helperText="At least 10 characters"
              sx={fieldSx}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="medium" sx={fieldSx}>
                <InputLabel id="cat-label">Category</InputLabel>
                <Select
                  labelId="cat-label"
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#0b201b',
                        color: '#edf5e8',
                        border: '1px solid rgba(215,245,173,0.25)',
                        '& .MuiMenuItem-root:hover': {
                          bgcolor: 'rgba(215,245,173,0.12)',
                        },
                        '& .Mui-selected': {
                          bgcolor: 'rgba(215,245,173,0.15) !important',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="bug">Bug</MenuItem>
                  <MenuItem value="feature">Feature idea</MenuItem>
                  <MenuItem value="question">Question</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Plugin version"
                fullWidth
                value={pluginVersion}
                onChange={(e) => setPluginVersion(e.target.value)}
                placeholder="2.0.0"
                sx={fieldSx}
              />
              <TextField
                label="QGIS version (optional)"
                fullWidth
                value={qgisVersion}
                onChange={(e) => setQgisVersion(e.target.value)}
                placeholder="e.g. 3.34"
                sx={fieldSx}
              />
            </Stack>
            <TextField
              label="Email (optional)"
              type="email"
              fullWidth
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="If we need to follow up"
              sx={fieldSx}
            />

            {/* Always-visible primary submit */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={!canSubmit}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              sx={{
                mt: 1,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '1rem',
                borderRadius: 2,
                bgcolor: '#1a73e8',
                boxShadow: '0 8px 24px rgba(26,115,232,0.4)',
                '&:hover': { bgcolor: '#1557b0' },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(26,115,232,0.35)',
                  color: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              {submitting ? 'Posting…' : 'Submit issue'}
            </Button>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(234,242,223,0.5)', textAlign: 'center' }}
            >
              No account required. Do not paste API keys or passwords in the description.
            </Typography>
          </Stack>
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#edf5e8' }}>
          Open issues
        </Typography>
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#d7f5ad' }} />
          </Box>
        ) : issues.length === 0 ? (
          <Typography sx={{ color: '#b8cabd' }}>
            No open issues yet. Be the first to post.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {issues.map((issue) => (
              <Card
                key={issue.id}
                elevation={0}
                sx={{
                  bgcolor: 'rgba(16,41,35,0.55)',
                  border: '1px solid rgba(215,245,173,0.16)',
                  borderRadius: 2,
                  color: '#edf5e8',
                }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ sm: 'flex-start' }}
                    sx={{ mb: 1 }}
                  >
                    <Typography fontWeight={700}>{issue.title}</Typography>
                    <Stack direction="row" spacing={0.75}>
                      <Chip
                        size="small"
                        label={CATEGORY_LABELS[issue.category] || issue.category}
                        sx={{ bgcolor: 'rgba(215,245,173,0.15)', color: '#d7f5ad' }}
                      />
                      <Chip
                        size="small"
                        label={issue.status.replace('_', ' ')}
                        color={statusColor(issue.status) as 'default'}
                      />
                    </Stack>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#b8cabd',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.55,
                      mb: 1,
                    }}
                  >
                    {issue.description.length > 400
                      ? `${issue.description.slice(0, 400)}…`
                      : issue.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(234,242,223,0.5)' }}>
                    {new Date(issue.created_at).toLocaleString()}
                    {issue.plugin_version ? ` · plugin ${issue.plugin_version}` : ''}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid rgba(215,245,173,0.12)', mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              component={RouterLink}
              to="/qgis-plugins"
              sx={{ textTransform: 'none', color: '#94A3B8', fontWeight: 600 }}
            >
              ← All QGIS Plugins
            </Button>
            <Button
              component={RouterLink}
              to="/digipin-encoder"
              sx={{ textTransform: 'none', color: '#d7f5ad', fontWeight: 600 }}
            >
              DIGIPIN Encoder home
            </Button>
          </Stack>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
};

export default DigipinEncoderIssuesPage;
